import { useAuth, useServiceCategories } from 'hooks';
import { BoardingPath, DaycarePath, ServicesPath, TrainingPath, TransportationPath, WalkingPath } from 'routes/Paths';

export type ServiceSetupCategory =
    | 'grooming'
    | 'daycare'
    | 'boarding'
    | 'transportation'
    | 'training'
    | 'walking'
    | 'evaluation';

type ServiceCategories = ReturnType<typeof useServiceCategories>;

const ADD_ONS_PATH = `${ServicesPath}#add_ons`;
const EVALUATION_PATH = `${ServicesPath}#evaluation`;

/**
 * Evaluation has no on/off switch in Business Settings: it is turned on and off from inside its own
 * setup page. Gating that page on `hasEvaluation` locked a business out of ever re-enabling it, so
 * access depends on the categories an evaluation can be applied to (grooming / daycare / boarding)
 * instead of the evaluation flag. `hasEvaluation` stays in the check so a business that already has
 * it on always keeps a way to turn it off. SU-2432
 */
export const canSetupEvaluation = (
    categories: Pick<ServiceCategories, 'hasEvaluation' | 'hasGrooming' | 'hasDaycare' | 'hasBoarding'>
): boolean => categories.hasEvaluation || categories.hasGrooming || categories.hasDaycare || categories.hasBoarding;

const isServiceCategoryEnabled = (category: ServiceSetupCategory, categories: ServiceCategories): boolean => {
    switch (category) {
        case 'grooming':
            return categories.hasGrooming;
        case 'daycare':
            return categories.hasDaycare;
        case 'boarding':
            return categories.hasBoarding;
        case 'transportation':
            return categories.hasTransportation;
        case 'training':
            return categories.hasTraining;
        case 'walking':
            return categories.hasWalking;
        case 'evaluation':
            return canSetupEvaluation(categories);
        default:
            return false;
    }
};

export const getFirstEnabledServiceCategoryPath = (categories: ServiceCategories): string => {
    if (categories.hasGrooming) {
        return ServicesPath;
    }
    if (categories.hasDaycare) {
        return DaycarePath;
    }
    if (categories.hasBoarding) {
        return BoardingPath;
    }
    if (categories.hasTransportation) {
        return TransportationPath;
    }
    if (categories.hasTraining) {
        return TrainingPath;
    }
    if (categories.hasWalking) {
        return WalkingPath;
    }
    if (categories.hasEvaluation) {
        return EVALUATION_PATH;
    }

    return ADD_ONS_PATH;
};

export const useServiceCategoryAccess = (category: ServiceSetupCategory | null) => {
    const auth = useAuth();
    const categories = useServiceCategories();
    const redirectTo = getFirstEnabledServiceCategoryPath(categories);
    const isAllowed = !auth.signedIn || category === null || isServiceCategoryEnabled(category, categories);

    return { isAllowed, redirectTo };
};

export default useServiceCategoryAccess;
